import {
  Activity,
  AlertTriangle,
  ArchiveRestore,
  Database,
  FileDown,
  HardDrive,
  Loader2,
  ShieldCheck,
  Trash2,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VISOR_MODULE_CARD_TITLE } from "@/modules/workModuleCore";
import { formatDataBytes, formatDataDate } from "@/lib/useOperationalDataManagement";

const MODULE_LABELS = {
  modulo_visor_sat: VISOR_MODULE_CARD_TITLE,
  modulo_asistente_laboral: "Asistente Laboral",
  modulo_resico: "RESICO (Confronta)",
};

function ModuleBadge({ active, label }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium ${
        active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500 line-through"
      }`}
    >
      {label}
    </span>
  );
}

/**
 * Secciones operativas de salud, respaldo y restauración.
 * @param {object} props
 * @param {"page"|"embedded"} [props.variant]
 * @param {ReturnType<import("@/lib/useOperationalDataManagement").useOperationalDataManagement>} props.dm
 * @param {boolean} [props.showDesignerExtras]
 */
export default function OperationalDataToolsSections({
  variant = "page",
  dm,
  showDesignerExtras = false,
}) {
  const {
    isPlatformDesigner,
    health,
    backups,
    modules,
    loading,
    busyBackup,
    restoreTarget,
    setRestoreTarget,
    busyRestore,
    maintenanceJob,
    maintProgress,
    maintMessage,
    securityKeys,
    createBackup,
    confirmRestore,
    runMaintenance,
  } = dm;

  const showDesignerDetail = showDesignerExtras && isPlatformDesigner;
  const sectionClass =
    variant === "embedded"
      ? "rounded-md border border-slate-200 bg-white shadow-sm p-4 space-y-3"
      : "rounded-sm border border-slate-200 bg-white p-5 space-y-3";

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="operational-data-tools-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Cargando…
      </div>
    );
  }

  return (
    <>
      {showDesignerDetail && securityKeys && !securityKeys.production_ready ? (
        <div
          className="rounded-sm border border-rose-200 bg-rose-50/90 p-4 text-[11px] text-rose-950 space-y-1"
          data-testid="gestion-datos-preflight-banner"
        >
          <p className="font-bold flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Pre-flight de producción: configuración pendiente
          </p>
          {!securityKeys.fiel_encryption_key?.valid ? <p>{securityKeys.fiel_encryption_key?.message}</p> : null}
          {!securityKeys.storage?.ok ? <p>{securityKeys.storage?.message}</p> : null}
          <p className="text-[10px] text-rose-800">
            Configure <code className="font-mono bg-rose-100 px-1 rounded">FIEL_ENCRYPTION_KEY</code> en Railway
            (Variables) antes de usar descargas SAT. Revise también Configuración → Gestión de llaves.
          </p>
        </div>
      ) : null}

      <section className={sectionClass} data-testid="gestion-datos-salud">
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#0F2D5C]" />
          Salud del sistema
        </h2>
        <div
          className={`text-xs rounded-sm px-3 py-2 ${
            health?.health_status === "ok"
              ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
              : "bg-amber-50 text-amber-900 border border-amber-200"
          }`}
        >
          {health?.health_message}
        </div>
        {showDesignerDetail ? (
          <>
            <div className="grid sm:grid-cols-2 gap-3 text-[11px]">
              <div className="border border-slate-100 rounded-sm p-3">
                <p className="font-medium text-slate-700 mb-2">Módulos activos</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(MODULE_LABELS).map(([key, label]) => (
                    <ModuleBadge key={key} active={Boolean(modules[key])} label={label} />
                  ))}
                </div>
              </div>
              <div className="border border-slate-100 rounded-sm p-3">
                <p className="font-medium text-slate-700 mb-1">Último respaldo</p>
                <p className="text-slate-600">
                  {formatDataDate(health?.last_backup?.created_at || health?.last_backup?.exported_at)}
                </p>
                <p className="text-slate-500 mt-1">{health?.backup_count ?? 0} respaldo(s) en servidor (máx. 10)</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <ShieldCheck className="w-3.5 h-3.5" />
              Auditoría de contratos: {health?.audit_log?.active ? "activa" : "sin registros aún"} (
              {health?.audit_log?.path || "logs/"})
            </div>
          </>
        ) : (
          <div className="border border-slate-100 rounded-sm p-3 text-[11px]">
            <p className="font-medium text-slate-700 mb-1">Último respaldo</p>
            <p className="text-slate-600">
              {formatDataDate(health?.last_backup?.created_at || health?.last_backup?.exported_at)}
            </p>
            <p className="text-slate-500 mt-1">{health?.backup_count ?? 0} respaldo(s) disponibles en el servidor</p>
          </div>
        )}
      </section>

      {showDesignerDetail && modules.modulo_visor_sat ? (
        <section className={sectionClass}>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Database className="w-4 h-4" /> {VISOR_MODULE_CARD_TITLE}
          </h3>
          <p className="text-[11px] text-slate-600">
            Clientes, CFDI, Excel/SAT, conciliación y configuración BNT incluidos en el respaldo unificado.
          </p>
        </section>
      ) : null}

      {showDesignerDetail && modules.modulo_asistente_laboral ? (
        <section className={sectionClass}>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <HardDrive className="w-4 h-4" /> Asistente Laboral
          </h3>
          <p className="text-[11px] text-slate-600">
            Trabajadores, registros patronales, REPSE, plantillas y diseños de contrato incluidos al generar respaldo.
          </p>
        </section>
      ) : null}

      {showDesignerDetail && modules.modulo_resico ? (
        <section className={sectionClass}>
          <h3 className="text-sm font-semibold">RESICO</h3>
          <p className="text-[11px] text-slate-600">
            Los datos de confronta RESICO forman parte del módulo {VISOR_MODULE_CARD_TITLE} y se respaldan con los CFDI
            y clientes.
          </p>
        </section>
      ) : null}

      <section id="respaldo" className={`${sectionClass} scroll-mt-6`}>
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <FileDown className="w-4 h-4 text-[#0F2D5C]" />
          Respaldo unificado
        </h2>
        <p className="text-[11px] text-slate-600">
          Genera un respaldo JSON en el servidor con todos los módulos activos. Rotación automática: se conservan los
          últimos 10 archivos (FIFO).
        </p>
        <Button
          type="button"
          className="h-9 text-xs bg-[#0F2D5C] hover:bg-[#0a1f42]"
          disabled={busyBackup || Boolean(maintenanceJob)}
          onClick={createBackup}
          data-testid="operational-create-backup-btn"
        >
          {busyBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          Generar respaldo ahora
        </Button>
      </section>

      <section id="restauracion" className={`${sectionClass} scroll-mt-6`}>
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <ArchiveRestore className="w-4 h-4 text-[#0F2D5C]" />
          Restauración
        </h2>
        {backups.length === 0 ? (
          <p className="text-[11px] text-slate-500">No hay respaldos en el servidor. Genere uno primero.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-[11px]">
            {backups.map((b) => (
              <li key={b.id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-slate-800">{b.filename}</p>
                  <p className="text-slate-500">
                    {formatDataDate(b.created_at)} · {formatDataBytes(b.size_bytes)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  disabled={busyRestore}
                  onClick={() => setRestoreTarget(b)}
                >
                  Restaurar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {showDesignerDetail ? (
        <section id="mantenimiento" className={`${sectionClass} space-y-4 scroll-mt-6`}>
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Wrench className="w-4 h-4 text-[#0F2D5C]" />
            Mantenimiento preventivo
          </h2>
          <p className="text-[11px] text-slate-600">
            Antes de limpiar, se crea un <strong>snapshot obligatorio</strong>. Luego se eliminan logs con más de 30
            días y archivos huérfanos en almacenamiento local.
          </p>
          {maintenanceJob ? (
            <div className="space-y-2">
              <Progress value={maintProgress} className="h-2" />
              <p className="text-[11px] text-slate-600">{maintMessage || "Ejecutando…"}</p>
            </div>
          ) : (
            <Button type="button" variant="secondary" className="h-9 text-xs" onClick={runMaintenance}>
              <Trash2 className="w-4 h-4 mr-2" />
              Ejecutar mantenimiento
            </Button>
          )}
        </section>
      ) : null}

      <AlertDialog open={Boolean(restoreTarget)} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restauración irreversible</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Se <strong>sustituirán los datos operativos actuales</strong> por el contenido del respaldo:
                </p>
                <p className="font-mono text-xs bg-slate-100 p-2 rounded">{restoreTarget?.filename}</p>
                <p className="text-rose-700 font-medium">
                  Esta acción no se puede deshacer. Asegúrese de que el respaldo es el correcto.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyRestore}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-700 hover:bg-rose-800"
              disabled={busyRestore}
              onClick={(e) => {
                e.preventDefault();
                confirmRestore();
              }}
            >
              {busyRestore ? "Restaurando…" : "Sí, restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
